<template>
  <div>
    <div v-if="dialogVisible && payReady" class="subscription-pay-overlay">
      <div class="subscription-pay-dialog">
      <button class="subscription-pay-close" type="button" @click="handleCancel">
        ✕
      </button>
      <h2 class="subscription-pay-title" style="margin-top: 0.71429rem;">{{ planTitle }}<span v-if="isEnterprise">·{{plan.seat}}席位</span></h2>
      <p class="subscription-pay-subtitle">{{ monthlyPointsLabel }}<span v-if="isEnterprise">(团队)</span></p>

      <div class="subscription-pay-price-row">
        <span class="subscription-pay-price">¥{{ formatPriceFromFen(plan.sellingPrice * (plan.seat || 1)) }}</span>
        <span
          v-if="plan.originalPrice > plan.sellingPrice"
          class="subscription-pay-original"
        >
          ¥{{ formatPriceFromFen(plan.originalPrice * (plan.seat || 1)) }}
        </span>
      </div>

      <div class="subscription-pay-qr">
        <img
          v-if="qrCodeImgUrl"
          class="subscription-pay-qr-image"
          :src="qrCodeImgUrl"
          alt=""
        />
        <div v-else-if="orderLoading" class="subscription-pay-qr-placeholder">
          二维码加载中...
        </div>
        <div v-else class="subscription-pay-qr-placeholder">
          {{ qrPlaceholderText }}
        </div>
      </div>

      <div class="subscription-pay-methods">
        <label class="subscription-pay-method">
          <input
            v-model="paymentMethod"
            type="radio"
            value="2"
            @change="handlePaymentMethodChange"
          />
          <span>支付宝</span>
        </label>
        <label class="subscription-pay-method">
          <input
            v-model="paymentMethod"
            type="radio"
            value="1"
            @change="handlePaymentMethodChange"
          />
          <span>微信</span>
        </label>
      </div>

      <p class="subscription-pay-tip">扫码支付后自动开通</p>

      <div class="subscription-pay-actions">
        <button
          type="button"
          class="subscription-pay-cancel"
          :disabled="orderLoading"
          @click="handleCancel"
        >
          取消
        </button>
        <button
          type="button"
          class="subscription-pay-confirm"
          :disabled="orderLoading || !outTradeNo"
          @click="handlePaid"
        >
          已支付
        </button>
      </div>
      </div>
    </div>

    <subscription-pay-result-dialog
      :visible.sync="payResultVisible"
      @confirm="finalizePaySuccess"
    />
  </div>
</template>

<script>
import QRCode from "qrcode";
import bus from "@/bus.js";
import SubscriptionPayResultDialog from "@/views/subscription/SubscriptionPayResultDialog.vue";
import {
  cycleToPeriodType,
  formatPriceFromFen,
  getCycleLabel,
  getPaymentTypeName,
  isApiSuccess,
  normalizeSubscriptionSeat,
  persistUserFields,
} from "@/utils/subscription";

export default {
  name: "SubscriptionPayDialog",
  components: {
    SubscriptionPayResultDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      default: null,
    },
    cycle: {
      type: String,
      default: "month",
    },
  },
  data() {
    return {
      paymentMethod: "2",
      qrCodeImgUrl: "",
      outTradeNo: "",
      orderLoading: false,
      payReady: false,
      pollTimerId: null,
      payResultVisible: false,
      pendingSuccessType: "",
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    isEnterprise() {
      const accountType = this.$store.state.subscriptionAccountType;
      return accountType === "enterprise" || accountType === "team";
    },
    planTitle() {
      if (!this.plan) {
        return "";
      }
      return `${this.plan.name} · ${getCycleLabel(this.cycle)}`;
    },
    monthlyPointsLabel() {
      if (!this.plan) {
        return "";
      }
      return `${this.formatNumber(this.plan.monthlyIntegral)} 积分/月`;
    },
    qrPlaceholderText() {
      return Number(this.paymentMethod) === 1 ? "微信扫码支付" : "支付宝扫码支付";
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetPayState();
        this.createOrder();
      } else {
        this.stopPolling();
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    resetPayState() {
      this.paymentMethod = "2";
      this.qrCodeImgUrl = "";
      this.outTradeNo = "";
      this.orderLoading = false;
      this.payReady = false;
      this.pendingSuccessType = "";
    },
    handlePaymentMethodChange() {
      this.createOrder();
    },
    closeWithoutPayReady() {
      this.dialogVisible = false;
      this.$emit("close");
    },
    handleCancel() {
      this.closeWithoutPayReady();
    },
    handlePaid() {
      this.checkPayState().then((success) => {
        if (success) {
          return;
        }
        if (this.$message) {
          this.$message.warning("暂未检测到支付成功，请完成扫码后再试");
        }
      });
    },
    createOrder() {
      if (!this.plan || !this.plan.configId) {
        return;
      }

      this.stopPolling();
      this.qrCodeImgUrl = "";
      this.outTradeNo = "";
      this.orderLoading = true;

      const paymentType = getPaymentTypeName(this.paymentMethod);
      this.$axios
        .post(
          `${this.$store.state.serverUrl}ytb/subscription/pay/create/${paymentType}`,
          {
            configId: Number(this.plan.configId),
            periodType: cycleToPeriodType(this.cycle),
            seats: normalizeSubscriptionSeat(this.plan.seat),
          },
        )
        .then(async (res) => {
          if (!isApiSuccess(res.data.code)) {
            if (this.$message) {
              this.$message.error(res.data.message || "创建订单失败");
            }
            this.closeWithoutPayReady();
            return;
          }

          const data = res.data.data || {};
          const qrSource = data.codeUrl || data.key;
          const tradeNo = data.outTradeNo || data.key;
          if (!qrSource || !tradeNo) {
            if (this.$message) {
              this.$message.error("未获取到支付信息");
            }
            this.closeWithoutPayReady();
            return;
          }

          this.outTradeNo = tradeNo;
          try {
            this.qrCodeImgUrl = await QRCode.toDataURL(String(qrSource), {
              width: 220,
              margin: 1,
            });
          } catch (error) {
            if (this.$message) {
              this.$message.error("二维码生成失败");
            }
            this.closeWithoutPayReady();
            return;
          }

          this.payReady = true;
          this.pollTimerId = setInterval(() => {
            this.checkPayState("automaticPolling");
          }, 1500);
        })
        .catch(() => {
          if (this.$message) {
            this.$message.error("创建订单失败");
          }
          this.closeWithoutPayReady();
        })
        .finally(() => {
          this.orderLoading = false;
        });
    },
    checkPayState(type) {
      if (!this.outTradeNo) {
        return Promise.resolve(false);
      }

      const paymentType = getPaymentTypeName(this.paymentMethod);
      return this.$axios
        .get(`${this.$store.state.serverUrl}ytb/subscription/pay/state`, {
          params: {
            outTradeNo: this.outTradeNo,
            paymentType,
          },
          multipleCancel: true,
        })
        .then((res) => {
          if (isApiSuccess(res.data.code) && res.data.data === "SUCCESS") {
            this.handlePaySuccess(type);
            return true;
          }
          return false;
        })
        .catch(() => false);
    },
    handlePaySuccess(type) {
      this.stopPolling();
      this.dialogVisible = false;
      this.pendingSuccessType = type || "";
      this.refreshPaySuccessData();
      this.payResultVisible = true;
    },
    finalizePaySuccess() {
      this.payResultVisible = false;
      this.$emit("success", {
        plan: this.plan,
        cycle: this.cycle,
        auto: this.pendingSuccessType === "automaticPolling",
      });
      this.pendingSuccessType = "";
    },
    refreshPaySuccessData() {
      this.refreshUserInfo();
      this.refreshTeamInfo();
      this.$store.dispatch("fetchUserSubscription");
      bus.$emit("allPoints");
    },
    refreshUserInfo() {
      this.$axios
        .get(`${this.$store.state.serverUrl}ytb/user/select/userById`)
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            return;
          }
          const data = res.data.data || {};
          persistUserFields({ vipType: data.vipType });
          this.$store.commit("setUserSubscriptionInfo", {
            vipType: Number(data.vipType || 0),
          });
        });
    },
    refreshTeamInfo() {
      const serverUrl = this.$store.state.serverUrl;
      this.$axios
        .get(`${serverUrl}ytb/user/client/team/overview`)
        .then((res) => {
          if (isApiSuccess(res.data.code)) {
            bus.$emit("teamOverviewUpdated", res.data.data || {});
          }
        })
        .catch(() => {});
      this.$axios
        .post(`${serverUrl}ytb/user/client/user/page`, {})
        .then((res) => {
          if (isApiSuccess(res.data.code)) {
            // 新结构 data 为树形数组
            bus.$emit("teamMembersUpdated", res.data.data || []);
          }
        })
        .catch(() => {});
    },
    stopPolling() {
      if (this.pollTimerId) {
        clearInterval(this.pollTimerId);
        this.pollTimerId = null;
      }
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    formatPriceFromFen,
  },
};
</script>

<style lang="less" scoped>
/* 尺寸按设计稿 px/14 转 rem；字号用 max(12PX, Nrem) 保证小屏不低于 12px */

.subscription-pay-overlay {
  position: fixed;
  inset: 0;
  z-index: 3100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.72);
}

.subscription-pay-dialog {
  position: relative;
  width: 30rem;
  padding: 2rem 2.28571rem 1.71429rem;
  border-radius: 1.14286rem;
  background: #12121e;
  box-shadow: 0 1.71429rem 4.57143rem rgba(0, 0, 0, 0.45);
  text-align: center;
}

.subscription-pay-close {
  position: absolute;
  top: 1.14286rem;
  left: 1.14286rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #8b93a7;
  cursor: pointer;
  font-size: ~"max(12PX, 1rem)";
  line-height: 1;
}

.subscription-pay-title {
  margin: 0;
  color: #ffffff;
  font-size: ~"max(12PX, 1.14286rem)";
  line-height: 2.28571rem;
  font-weight: 700;
}

.subscription-pay-subtitle {
  margin: 0.42857rem 0 0;
  color: #8b93a7;
  font-size: ~"max(12PX, 1rem)";
}

.subscription-pay-price-row {
  margin-top: 1.28571rem;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.71429rem;
}

.subscription-pay-price {
  color: #f0b90b;
  font-size: ~"max(12PX, 2.42857rem)";
  font-weight: 800;
  line-height: 1;
}

.subscription-pay-original {
  color: #5d6476;
  font-size: ~"max(12PX, 1.14286rem)";
  text-decoration: line-through;
}

.subscription-pay-qr {
  width: 15.71429rem;
  height: 15.71429rem;
  margin: 1.57143rem auto 0;
  border-radius: 0.85714rem;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.subscription-pay-qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.subscription-pay-qr-placeholder {
  color: #6b7280;
  font-size: ~"max(12PX, 1rem)";
}

.subscription-pay-methods {
  margin-top: 1.28571rem;
  display: flex;
  justify-content: center;
  gap: 2rem;
}

.subscription-pay-method {
  display: inline-flex;
  align-items: center;
  gap: 0.57143rem;
  color: #d8deea;
  font-size: ~"max(12PX, 1rem)";
  cursor: pointer;

  input {
    width: 1.14286rem;
    height: 1.14286rem;
    margin: 0;
    accent-color: #8b5cf6;
    cursor: pointer;
  }
}

.subscription-pay-tip {
  margin: 0.71429rem 0 0;
  color: #6f778b;
  font-size: ~"max(12PX, 0.85714rem)";
}

.subscription-pay-actions {
  margin-top: 1.57143rem;
  display: flex;
  gap: 0.85714rem;
}

.subscription-pay-cancel,
.subscription-pay-confirm {
  flex: 1;
  height: 3rem;
  border-radius: 0.57143rem;
  font-size: ~"max(12PX, 1.07143rem)";
  font-weight: 700;
  cursor: pointer;
}

.subscription-pay-cancel {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: #d8deea;
}

.subscription-pay-confirm {
  border: none;
  background: #8b5cf6;
  color: #ffffff;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}
</style>
